import React, {useState} from "react";
import {Button, Input, Switch, Table, Pagination, Spin} from "antd";
import {useTranslation} from "react-i18next";
import {useHistory} from "react-router-dom";
import {useStore} from "../../../../../hooks/useStore";
import {subCompanyShiftOptionsTypes} from "./SubCompanyShiftOptionsTypes";
import {
    SubCompanyContainer,
    SubCompanyTableWrap,
    SubCompanyCardList,
    SubCompanyCard,
    SubCompanyCardTitle,
    SubCompanyCardRow,
    SubCompanyCardActions
} from "../../optionsStyles";
import {EmptyMessage} from "../../../../_defaultComponents/EmptyMessage/EmptyMessage";

const SubCompanyShiftOptions = ({
    subCompanyData,
    subCompanyLoading,
    currentPage,
    setCurrentPage,
    companyId,
    subCompanyRecallRef,
    setSubCompanyRecall,
    setSubCompanyData
}: subCompanyShiftOptionsTypes) => {
    const {t} = useTranslation();
    const history = useHistory();
    const stores = useStore();
    const [showSubmitButton, setShowSubmitButton] = useState({});
    const [isSubCompanySubmitting, setIsSubCompanySubmitting] = useState(false);

    const openErrorModal = (submitData, key) => {
        stores.appStore.openErrorModal({
            title: `${t("modal.errorTitle", {ns: "common"})}!`,
            content: `${submitData.name}, ${t(`fields.shiftOptions.${key}.label`, {
                ns: "common"
            })} ${t("validation.greaterThanZero", {
                ns: "common"
            })}`,
            onOk: () => {}
        });
    };
    const onSubCompanySubmit = index => {
        setIsSubCompanySubmitting(true);
        let submitData = subCompanyData.rows[index]?.options?.shift;
        submitData.name = subCompanyData.rows[index]?.name;
        submitData._id = subCompanyData.rows[index]?._id;

        const submitKeys = Object.keys(submitData);
        let hasError = false;

        submitKeys.forEach(key => {
            if (["shiftControlIsActive", "distanceAlertIsActive", "packageLimitAlertIsActive"].includes(key)) return;
            if (key === "distanceLimit" && submitData["distanceAlertIsActive"] === false) return;

            if (submitData[key] <= 0 || submitData[key]?.length === 0) {
                openErrorModal(submitData, key);
                hasError = true;
            }
        });

        setTimeout(async () => {
            if (hasError) {
                setIsSubCompanySubmitting(false);
                return;
            }

            const params = {
                subCompanyId: submitData._id,
                options: {
                    distanceAlertIsActive: submitData?.distanceAlertIsActive,
                    distanceLimit: submitData?.distanceLimit,
                    shiftControlIsActive: submitData?.shiftControlIsActive,
                    earliestShiftStart: submitData?.earliestShiftStart,
                    latestShiftEnd: submitData?.latestShiftEnd,
                    maxShiftPackageLimit: submitData?.maxShiftPackageLimit,
                    packageLimitAlertIsActive: submitData?.packageLimitAlertIsActive
                }
            };
            const result = await stores.companyStore.updateSubCompanyDistanceNotificationOptions(
                params
            );

            if (result) {
                setSubCompanyRecall(!subCompanyRecallRef.current);
            }
            setIsSubCompanySubmitting(false);
        }, 0);
    };

    const renderSwitch = key => {
        return (
            <Table.Column
                dataIndex={key}
                shouldCellUpdate={() => true}
                title={t(`fields.shiftOptions.${key}.short`, {ns: "common"})}
                render={(_, record, index) => {
                    const shift = subCompanyData.rows[index]?.options?.shift;
                    return (
                        <Switch
                            data-testid={key}
                            checked={shift[key]}
                            onChange={e => {
                                showSubmitButton[record._id] = true;
                                setSubCompanyData(prevData => {
                                    const newData = {...prevData};
                                    const newItem = {...newData.rows[index]};
                                    newItem.options.shift[key] = e;
                                    newData.rows[index] = newItem;
                                    return newData;
                                });
                            }}
                        />
                    );
                }}
            />
        );
    };

    const renderInput = (key, disabledKey) => {
        return (
            <Table.Column
                dataIndex={key}
                shouldCellUpdate={() => true}
                title={t(`fields.shiftOptions.${key}.short`, {ns: "common"})}
                render={(_, record, index) => {
                    const shift = subCompanyData.rows[index]?.options?.shift;
                    return (
                        <Input
                            disabled={!shift[disabledKey]}
                            type="number"
                            name={key}
                            data-testid={`${key}Input`}
                            min={0}
                            value={shift && shift[key]}
                            onChange={e => {
                                e.persist();
                                showSubmitButton[record._id] = true;
                                setSubCompanyData(prevData => {
                                    const newData = {...prevData};
                                    const newItem = {...newData.rows[index]};
                                    newItem.options.shift[key] = e.target?.value || "";
                                    newData.rows[index] = newItem;
                                    return newData;
                                });
                            }}
                        />
                    );
                }}
            />
        );
    };

    const updateRow = (index, field, value) => {
        setShowSubmitButton(prev => ({...prev, [subCompanyData.rows[index]._id]: true}));
        setSubCompanyData(prevData => {
            const newData = {...prevData};
            const newItem = {...newData.rows[index]};
            if (!newItem.options) newItem.options = {};
            if (!newItem.options.shift) newItem.options.shift = {};
            newItem.options.shift[field] = value;
            newData.rows[index] = newItem;
            return newData;
        });
    };

    return (
        <SubCompanyContainer>
            <SubCompanyTableWrap>
                <Table
                    rowKey="_id"
                    dataSource={subCompanyData?.rows}
                    loading={subCompanyLoading}
                    data-testid="subCompaniesTable"
                    pagination={{
                        total: subCompanyData.dataCount,
                        current: currentPage,
                        onChange: async page => {
                            setCurrentPage(page);
                            history.push("/companies/shift-options/" + companyId + "?page=" + page);
                        },
                        hideOnSinglePage: true,
                        showSizeChanger: false
                    }}
                    locale={{
                        emptyText: <EmptyMessage />
                    }}
                >
                    <Table.Column
                        dataIndex="name"
                        title={t("fields.subcompany", {ns: "common"})}
                        render={text => <span data-textid="name">{text}</span>}
                    />
                    <Table.Column
                        dataIndex="city"
                        title={t("fields.city", {ns: "common"})}
                        render={text => <span data-textid="city">{text}</span>}
                    />

                    {renderSwitch("shiftControlIsActive")}
                    {renderInput("earliestShiftStart", "shiftControlIsActive")}
                    {renderInput("latestShiftEnd", "shiftControlIsActive")}
                    {renderSwitch("distanceAlertIsActive")}
                    {renderInput("distanceLimit", "distanceAlertIsActive")}
                    {renderSwitch("packageLimitAlertIsActive")}
                    {renderInput("maxShiftPackageLimit", "packageLimitAlertIsActive")}

                    <Table.Column
                        dataIndex="_id"
                        shouldCellUpdate={() => true}
                        render={(text, _, index) =>
                            showSubmitButton[text] && (
                                <Button
                                    disabled={isSubCompanySubmitting}
                                    onClick={() => onSubCompanySubmit(index)}
                                    type="primary"
                                    data-testid="submitButton"
                                >
                                    {t("actions.save", {ns: "common"})}
                                </Button>
                            )
                        }
                    />
                </Table>
            </SubCompanyTableWrap>

            <SubCompanyCardList>
                {subCompanyLoading ? (
                    <div style={{textAlign: "center", padding: 24}}>
                        <Spin size="large" />
                    </div>
                ) : !subCompanyData?.rows?.length ? (
                    <EmptyMessage />
                ) : (
                    <>
                        {subCompanyData.rows.map((record, index) => {
                            const s = record?.options?.shift || {};
                            return (
                                <SubCompanyCard key={record._id}>
                                    <SubCompanyCardTitle>
                                        {record.name} · {record.city}
                                    </SubCompanyCardTitle>
                                    <SubCompanyCardRow>
                                        <span className="subcompany-card-label">
                                            {t("fields.shiftOptions.shiftControlIsActive.label", {ns: "common"})} (
                                            {t("fields.shiftOptions.shiftControlIsActive.short", {ns: "common"})})
                                        </span>
                                        <Switch
                                            checked={s.shiftControlIsActive}
                                            checkedChildren={t("other.active", {ns: "common"})}
                                            unCheckedChildren={t("other.passive", {ns: "common"})}
                                            onChange={e => updateRow(index, "shiftControlIsActive", e)}
                                        />
                                    </SubCompanyCardRow>
                                    <SubCompanyCardRow>
                                        <span className="subcompany-card-label">
                                            {t("fields.shiftOptions.earliestShiftStart.label", {ns: "common"})} (
                                            {t("fields.shiftOptions.earliestShiftStart.short", {ns: "common"})})
                                        </span>
                                        <Input
                                            type="number"
                                            disabled={!s.shiftControlIsActive}
                                            value={s.earliestShiftStart}
                                            onChange={e => updateRow(index, "earliestShiftStart", e.target?.value ?? "")}
                                        />
                                    </SubCompanyCardRow>
                                    <SubCompanyCardRow>
                                        <span className="subcompany-card-label">
                                            {t("fields.shiftOptions.latestShiftEnd.label", {ns: "common"})} (
                                            {t("fields.shiftOptions.latestShiftEnd.short", {ns: "common"})})
                                        </span>
                                        <Input
                                            type="number"
                                            disabled={!s.shiftControlIsActive}
                                            value={s.latestShiftEnd}
                                            onChange={e => updateRow(index, "latestShiftEnd", e.target?.value ?? "")}
                                        />
                                    </SubCompanyCardRow>
                                    <SubCompanyCardRow>
                                        <span className="subcompany-card-label">
                                            {t("fields.shiftOptions.distanceAlertIsActive.label", {ns: "common"})} (
                                            {t("fields.shiftOptions.distanceAlertIsActive.short", {ns: "common"})})
                                        </span>
                                        <Switch
                                            checked={s.distanceAlertIsActive}
                                            checkedChildren={t("other.active", {ns: "common"})}
                                            unCheckedChildren={t("other.passive", {ns: "common"})}
                                            onChange={e => updateRow(index, "distanceAlertIsActive", e)}
                                        />
                                    </SubCompanyCardRow>
                                    <SubCompanyCardRow>
                                        <span className="subcompany-card-label">
                                            {t("fields.shiftOptions.distanceLimit.label", {ns: "common"})} (
                                            {t("fields.shiftOptions.distanceLimit.short", {ns: "common"})})
                                        </span>
                                        <Input
                                            type="number"
                                            disabled={!s.distanceAlertIsActive}
                                            value={s.distanceLimit}
                                            onChange={e => updateRow(index, "distanceLimit", e.target?.value ?? "")}
                                        />
                                    </SubCompanyCardRow>
                                    <SubCompanyCardRow>
                                        <span className="subcompany-card-label">
                                            {t("fields.shiftOptions.packageLimitAlertIsActive.label", {ns: "common"})} (
                                            {t("fields.shiftOptions.packageLimitAlertIsActive.short", {ns: "common"})})
                                        </span>
                                        <Switch
                                            checked={s.packageLimitAlertIsActive}
                                            checkedChildren={t("other.active", {ns: "common"})}
                                            unCheckedChildren={t("other.passive", {ns: "common"})}
                                            onChange={e => updateRow(index, "packageLimitAlertIsActive", e)}
                                        />
                                    </SubCompanyCardRow>
                                    <SubCompanyCardRow>
                                        <span className="subcompany-card-label">
                                            {t("fields.shiftOptions.maxShiftPackageLimit.label", {ns: "common"})} (
                                            {t("fields.shiftOptions.maxShiftPackageLimit.short", {ns: "common"})})
                                        </span>
                                        <Input
                                            type="number"
                                            disabled={!s.packageLimitAlertIsActive}
                                            value={s.maxShiftPackageLimit}
                                            onChange={e => updateRow(index, "maxShiftPackageLimit", e.target?.value ?? "")}
                                        />
                                    </SubCompanyCardRow>
                                    <SubCompanyCardActions>
                                        {showSubmitButton[record._id] && (
                                            <Button
                                                disabled={isSubCompanySubmitting}
                                                onClick={() => onSubCompanySubmit(index)}
                                                type="primary"
                                                block
                                            >
                                                {t("actions.save", {ns: "common"})}
                                            </Button>
                                        )}
                                    </SubCompanyCardActions>
                                </SubCompanyCard>
                            );
                        })}
                        {subCompanyData?.dataCount > 10 && (
                            <div style={{marginTop: 16, display: "flex", justifyContent: "center"}}>
                                <Pagination
                                    total={subCompanyData.dataCount}
                                    current={currentPage}
                                    onChange={page => {
                                        setCurrentPage(page);
                                        history.push("/companies/shift-options/" + companyId + "?page=" + page);
                                    }}
                                    hideOnSinglePage
                                    showSizeChanger={false}
                                    pageSize={10}
                                />
                            </div>
                        )}
                    </>
                )}
            </SubCompanyCardList>
        </SubCompanyContainer>
    );
};

export default SubCompanyShiftOptions;
